/* BEATSEEK Emote Lab — premium + lite UI for AI-generated emotes.
 * Backend: POST /api/emote-lab/generate, /save, GET /mine, DELETE, /twitch-pack
 * Exports: <EmoteLabModal> (premium, DJ Tools), <EmoteLabLite> (basic, dashboard)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, Sparkles, Download, Trash2, Globe, Loader, Image as ImageIcon, Wand2, Plus, Share2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STYLES = [
  { id: 'classic', label: 'Classic', sub: 'Clean cartoon' },
  { id: 'neon',    label: 'Neon',    sub: 'Cyan + magenta glow' },
  { id: 'cartoon', label: 'Cartoon', sub: 'Bold comic-book' },
  { id: 'pixel',   label: 'Pixel',   sub: '16-bit retro' },
  { id: 'vinyl',   label: 'Vinyl',   sub: 'Vintage DJ' },
];

// Curated starter prompts — one-tap inspiration for new users. Each preset
// fills the prompt textarea AND auto-selects the style that fits best.
const EMOTE_PRESETS = [
  { label: '🎲 Surprise me', prompt: '', style: '', surprise: true },
  { label: '🎧 DJ Cat',       prompt: 'Cat wearing DJ headphones, smiling',                    style: 'cartoon' },
  { label: '🔥 Fire Hype',    prompt: 'Fire flames exploding outward, energetic',              style: 'neon' },
  { label: '💿 Spinning Vinyl', prompt: 'Glowing vinyl record spinning fast',                  style: 'vinyl' },
  { label: '🎉 Hands Up',     prompt: 'Pair of hands raised high in hype celebration',         style: 'cartoon' },
  { label: '💀 Neon Skull',   prompt: 'Skull with bright neon glow, electric aura',            style: 'neon' },
  { label: '👽 Alien Raver',  prompt: 'Tiny alien dancing at a rave with glow sticks',         style: 'pixel' },
  { label: '😴 Sleepy Synth', prompt: 'Sleepy face with synthwave glow, eyes closed',          style: 'pixel' },
  { label: '🥁 Drum Slap',    prompt: 'Hand slapping a drum kit, motion lines',                style: 'cartoon' },
];

// Loading-dot style (used while generating)
const dotStyle = (i) => ({
  width: '8px', height: '8px', borderRadius: '50%',
  background: '#22d3ee',
  display: 'inline-block', margin: '0 3px',
  animation: 'elabBounce 1s ease-in-out infinite',
  animationDelay: `${i * 0.15}s`,
});

// =============== SHARED HOOKS / UTILS ===============
function useEmoteCredits(userId) {
  const [credits, setCredits] = useState(null);
  const refresh = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await fetch(`${API}/api/emote-lab/credits/${userId}`);
      if (r.ok) setCredits(await r.json());
    } catch (_e) { /* ignore */ }
  }, [userId]);
  useEffect(() => { refresh(); }, [refresh]);
  return [credits, refresh];
}

function useMyEmotes(userId) {
  const [emotes, setEmotes] = useState([]);
  const refresh = useCallback(async () => {
    if (!userId) return;
    try {
      const r = await fetch(`${API}/api/emote-lab/mine/${userId}`);
      if (r.ok) {
        const d = await r.json();
        setEmotes(d.emotes || []);
      }
    } catch (_e) { /* ignore */ }
  }, [userId]);
  useEffect(() => { refresh(); }, [refresh]);
  return [emotes, refresh];
}

// =============== STYLE CHIPS ROW ===============
const StyleChips = ({ value, onChange, compact }) => (
  <div style={{
    display: 'flex', gap: '6px', flexWrap: 'wrap',
    justifyContent: compact ? 'flex-start' : 'center',
  }}>
    {STYLES.map(s => {
      const active = value === s.id;
      return (
        <button
          key={s.id}
          data-testid={`emote-style-${s.id}`}
          onClick={() => onChange(s.id)}
          style={{
            padding: compact ? '6px 10px' : '8px 14px',
            borderRadius: '999px',
            border: active ? '1px solid #22d3ee' : '1px solid rgba(255,255,255,0.12)',
            background: active
              ? 'linear-gradient(135deg, rgba(6,182,212,0.25), rgba(192,38,211,0.25))'
              : 'rgba(255,255,255,0.04)',
            color: active ? '#fff' : '#94a3b8',
            fontSize: compact ? '11px' : '12px',
            fontWeight: 700, letterSpacing: '0.04em',
            cursor: 'pointer',
            transition: 'all 0.18s',
            boxShadow: active ? '0 0 16px rgba(6,182,212,0.35)' : 'none',
          }}
        >
          {s.label}
        </button>
      );
    })}
  </div>
);

// =============== EMOTE LIBRARY GRID ITEM ===============
const LibraryCard = ({ emote, onDelete, onDownload, onTwitch, onAddToCustom, onShareWithFriend, premium }) => (
  <div
    data-testid={`emote-card-${emote.id}`}
    style={{
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '12px', padding: '10px',
      display: 'flex', flexDirection: 'column', gap: '8px',
    }}
  >
    <div style={{
      aspectRatio: '1', borderRadius: '8px', overflow: 'hidden',
      background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(34,211,238,0.18)',
    }}>
      <img
        src={`${API}/api/emote-lab/${emote.id}/file`}
        alt={emote.name}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    </div>
    <div style={{ fontSize: '11px', fontWeight: 700, color: '#fff', textAlign: 'center',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {emote.name}
    </div>
    {/* Travel stat — how far this emote has gone through the community */}
    <div
      data-testid={`emote-stats-${emote.id}`}
      title="Shares, copies & chat uses across the community"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        fontSize: '9px', letterSpacing: '0.06em', color: '#94a3b8',
        textTransform: 'uppercase',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: '#22d3ee' }}>
        ⬇ {emote.downloads || 0}
      </span>
      <span style={{ opacity: 0.4 }}>·</span>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', color: '#fbbf24' }}>
        ✦ Used {emote.uses || 0}×
      </span>
    </div>
    <button
      onClick={() => onAddToCustom(emote)}
      title="Add to your custom chat emotes"
      data-testid={`emote-to-custom-${emote.id}`}
      style={{
        width: '100%', padding: '3px 6px',
        background: 'linear-gradient(135deg, rgba(232,121,249,0.20), rgba(192,38,211,0.18))',
        border: '1px solid rgba(232,121,249,0.65)',
        color: '#e879f9', cursor: 'pointer',
        fontSize: '8px', fontWeight: 700, letterSpacing: '0.02em',
        borderRadius: '5px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
      }}
    >
      <Plus size={9} /> Add to Custom
    </button>
    <div style={{ display: 'flex', gap: '4px' }}>
      <button
        onClick={() => onDownload(emote)}
        title="Download PNG"
        data-testid={`emote-download-${emote.id}`}
        style={btnIcon('#22d3ee')}
      >
        <Download size={12} />
      </button>
      <button
        onClick={() => onShareWithFriend(emote)}
        title="Share with a friend"
        data-testid={`emote-share-friend-${emote.id}`}
        style={btnIcon('#fbbf24')}
      >
        <Share2 size={12} />
      </button>
      {premium && (
        <button
          onClick={() => onTwitch(emote)}
          title="Twitch Size Pack (112/56/28)"
          data-testid={`emote-twitch-${emote.id}`}
          style={btnIcon('#e879f9')}
        >
          TW
        </button>
      )}
      <button
        onClick={() => onDelete(emote)}
        title="Delete"
        data-testid={`emote-delete-${emote.id}`}
        style={btnIcon('#ef4444')}
      >
        <Trash2 size={12} />
      </button>
    </div>
  </div>
);

const btnIcon = (color) => ({
  flex: 1, padding: '5px', borderRadius: '6px',
  background: 'rgba(255,255,255,0.04)', border: `1px solid ${color}`,
  color, cursor: 'pointer', fontSize: '10px', fontWeight: 700,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
});

// =============== CORE LAB BODY (shared between premium + lite) ===============
const EmoteLabBody = ({ currentUser, premium, onClose, initialPrompt, initialStyle }) => {
  const userId = currentUser?.id;
  const [prompt, setPrompt] = useState(initialPrompt || '');
  const [style, setStyle] = useState(initialStyle || 'neon');
  const [variations, setVariations] = useState(1);
  const [isPublic, setIsPublic] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [previews, setPreviews] = useState([]);  // array of base64 strings
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [shareEmote, setShareEmote] = useState(null); // emote being shared with friend
  const [saving, setSaving] = useState(false);

  const [credits, refreshCredits] = useEmoteCredits(userId);
  const [myEmotes, refreshMine] = useMyEmotes(userId);

  const remaining = credits
    ? (premium ? credits.premium.flat_remaining : credits.basic.remaining)
    : null;
  const tier = premium ? 'premium' : 'basic';

  const handleGenerate = async () => {
    setError(null);
    if (!prompt.trim()) { setError('Type a description first'); return; }
    if (prompt.length > 280) { setError('Keep it under 280 characters'); return; }
    if (remaining !== null && remaining < variations) {
      setError(`Not enough credits — you have ${remaining} left`);
      return;
    }
    setGenerating(true);
    setPreviews([]);
    try {
      const r = await fetch(`${API}/api/emote-lab/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId, prompt, style, tier, variations,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Generation failed');
      setPreviews(data.images_b64 || []);
      refreshCredits();
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async (b64, defaultName) => {
    setSaving(true);
    try {
      const name = window.prompt('Name your emote (max 60 chars):', defaultName || prompt.slice(0, 30));
      if (!name) { setSaving(false); return; }
      const r = await fetch(`${API}/api/emote-lab/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId, name, image_b64: b64, prompt, style, tier,
          animated: false, is_public: isPublic,
        }),
      });
      if (!r.ok) {
        const d = await r.json();
        throw new Error(d.detail || 'Save failed');
      }
      // Remove this preview from the list (saved)
      setPreviews(prev => prev.filter(p => p !== b64));
      refreshMine();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (emote) => {
    if (!window.confirm(`Delete "${emote.name}"?`)) return;
    await fetch(`${API}/api/emote-lab/${emote.id}?user_id=${userId}`, { method: 'DELETE' });
    refreshMine();
  };

  const handleDownloadPng = (emote) => {
    const a = document.createElement('a');
    a.href = `${API}/api/emote-lab/${emote.id}/file?d=1`;
    a.download = `${emote.name.replace(/[^a-z0-9_-]/gi, '_')}.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const handleTwitchPack = (emote) => {
    const a = document.createElement('a');
    a.href = `${API}/api/emote-lab/${emote.id}/twitch-pack`;
    a.download = `${emote.name.replace(/[^a-z0-9_-]/gi, '_')}_twitch_pack.zip`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const handleAddToCustom = async (emote) => {
    // Suggest a default handle from the emote name — alphanum, max 8 chars.
    const suggested = (emote.name || 'emote').replace(/[^A-Za-z0-9]/g, '').slice(0, 8) || 'emote';
    const handle = window.prompt(
      'Pick a short chat name (1–8 letters/numbers).\nThis is how the emote will be triggered in chat:',
      suggested,
    );
    if (handle === null) return;
    const clean = (handle || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 8);
    if (!clean) { setError('Chat name must be 1–8 alphanumeric characters'); return; }
    try {
      const r = await fetch(`${API}/api/emote-lab/to-custom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, ai_emote_id: emote.id, chat_name: clean }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Add to custom emotes failed');
      setError(null);
      // Broadcast — any open chat panel listens to refresh its custom-emote list
      window.dispatchEvent(new CustomEvent('beatseek:custom-emotes-changed', {
        detail: { name: clean, id: data.id }
      }));
      // Surface a soft confirmation via the same banner — green-tinted
      setSuccessMsg(data.message || `Added '${clean}' to your custom emotes!`);
      setTimeout(() => setSuccessMsg(null), 3500);
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <>
      {/* Scoped styles: subtle dark scrollbar + bounce keyframes */}
      <style>{`
        .elab-presets-strip {
          scrollbar-width: thin;                                 /* Firefox */
          scrollbar-color: rgba(34,211,238,0.35) transparent;    /* Firefox */
        }
        .elab-presets-strip::-webkit-scrollbar {
          height: 5px;
        }
        .elab-presets-strip::-webkit-scrollbar-track {
          background: rgba(255,255,255,0.03);
          border-radius: 999px;
        }
        .elab-presets-strip::-webkit-scrollbar-thumb {
          background: rgba(34,211,238,0.35);
          border-radius: 999px;
        }
        .elab-presets-strip::-webkit-scrollbar-thumb:hover {
          background: rgba(34,211,238,0.55);
        }
        @keyframes elabBounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
      `}</style>

      {/* Credits banner */}
      {credits && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 14px', marginBottom: '14px',
          background: 'rgba(34,211,238,0.06)',
          border: '1px solid rgba(34,211,238,0.18)',
          borderRadius: '10px', fontSize: '12px',
        }}>
          <div style={{ color: '#22d3ee', fontWeight: 700 }}>
            {credits.is_admin ? '∞ Unlimited (admin)' : `${remaining} credits remaining`}
          </div>
          {!credits.is_admin && credits.basic.bonus_earned > 0 && (
            <div style={{ color: '#e879f9', fontSize: '10px' }}>
              +{credits.basic.bonus_earned} bonus earned 🎉
            </div>
          )}
        </div>
      )}

      {/* Prompt */}
      <label style={lbl}>Describe your emote</label>

      {/* Quick-start preset chips */}
      <div
        data-testid="emote-presets-strip"
        className="elab-presets-strip"
        style={{
          display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px',
          marginBottom: '8px',
        }}
      >
        {EMOTE_PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            data-testid={`emote-preset-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            onClick={() => {
              if (p.surprise) {
                const pool = EMOTE_PRESETS.filter(x => !x.surprise);
                const pick = pool[Math.floor(Math.random() * pool.length)];
                setPrompt(pick.prompt);
                setStyle(pick.style);
              } else {
                setPrompt(p.prompt);
                setStyle(p.style);
              }
            }}
            style={{
              flexShrink: 0,
              padding: '6px 10px',
              background: p.surprise
                ? 'linear-gradient(135deg, rgba(34,211,238,0.18), rgba(232,121,249,0.18))'
                : 'rgba(255,255,255,0.04)',
              border: p.surprise
                ? '1px solid rgba(34,211,238,0.45)'
                : '1px solid rgba(255,255,255,0.10)',
              color: p.surprise ? '#22d3ee' : '#cbd5e1',
              fontSize: '11px', fontWeight: 600,
              borderRadius: '999px', cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#22d3ee'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = p.surprise ? 'rgba(34,211,238,0.45)' : 'rgba(255,255,255,0.10)';
              e.currentTarget.style.color = p.surprise ? '#22d3ee' : '#cbd5e1';
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      <textarea
        data-testid="emote-prompt-input"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={2}
        maxLength={280}
        placeholder="e.g. DJ vinyl record with sunglasses giving thumbs up"
        style={{
          width: '100%', padding: '10px',
          background: 'rgba(255,255,255,0.04)', color: '#fff',
          border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px',
          fontSize: '13px', resize: 'vertical', boxSizing: 'border-box',
          fontFamily: 'inherit', outline: 'none',
        }}
      />
      <div style={{ fontSize: '9px', color: '#555', textAlign: 'right', marginBottom: '12px' }}>
        {prompt.length}/280
      </div>

      {/* Style chips */}
      <label style={lbl}>Pick your style</label>
      <StyleChips value={style} onChange={setStyle} compact={!premium} />

      {/* Premium options */}
      {premium && (
        <div style={{ display: 'flex', gap: '14px', marginTop: '14px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={variations === 3}
              onChange={(e) => setVariations(e.target.checked ? 3 : 1)}
              data-testid="emote-variations-toggle"
            />
            Generate 3 variations (3 credits)
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              data-testid="emote-public-toggle"
            />
            Share to public gallery
          </label>
        </div>
      )}

      {/* Generate button */}
      <button
        onClick={handleGenerate}
        disabled={generating || (remaining !== null && remaining < variations && !credits?.is_admin)}
        data-testid="emote-generate-btn"
        style={{
          width: '100%', marginTop: '14px', padding: '12px',
          background: 'linear-gradient(135deg, #06b6d4 0%, #c026d3 100%)',
          color: '#fff', border: 'none', borderRadius: '10px',
          fontWeight: 800, fontSize: '13px', letterSpacing: '0.06em',
          textTransform: 'uppercase', cursor: 'pointer',
          opacity: generating ? 0.7 : 1,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          boxShadow: '0 0 18px rgba(6,182,212,0.35)',
        }}
      >
        {generating ? <><Loader size={14} className="spin" /> Generating…</> : <><Sparkles size={14} /> Generate Emote</>}
      </button>

      {/* Error */}
      {error && (
        <div style={{
          marginTop: '10px', padding: '8px 12px', fontSize: '11px',
          color: '#ef4444', background: 'rgba(239,68,68,0.08)',
          borderRadius: '6px',
        }}>
          {error}
        </div>
      )}

      {/* Success */}
      {successMsg && (
        <div
          data-testid="emote-lab-success"
          style={{
            marginTop: '10px', padding: '8px 12px', fontSize: '11px',
            color: '#22c55e', background: 'rgba(34,197,94,0.08)',
            border: '1px solid rgba(34,197,94,0.25)',
            borderRadius: '6px', fontWeight: 600,
          }}
        >
          ✓ {successMsg}
        </div>
      )}

      {/* Loading preview placeholder */}
      {generating && previews.length === 0 && (
        <div
          data-testid="emote-generating-spinner"
          style={{
            marginTop: '20px', padding: '24px', textAlign: 'center',
            background: 'rgba(34,211,238,0.04)',
            border: '1px dashed rgba(34,211,238,0.3)',
            borderRadius: '12px',
          }}
        >
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '4px', marginBottom: '8px',
          }}>
            <span className="elab-dot" style={dotStyle(0)} />
            <span className="elab-dot" style={dotStyle(1)} />
            <span className="elab-dot" style={dotStyle(2)} />
          </div>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#22d3ee',
                        letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Painting your emote…
          </div>
          <div style={{ fontSize: '9px', color: '#64748b', marginTop: '4px' }}>
            Takes 10–25 seconds. Hang tight.
          </div>
        </div>
      )}

      {/* Preview grid (generated, unsaved) */}
      {previews.length > 0 && (
        <>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#22d3ee',
                        letterSpacing: '0.12em', marginTop: '20px', marginBottom: '10px',
                        textTransform: 'uppercase' }}>
            New Generation — Save the ones you love
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: previews.length === 1
              ? 'minmax(0, 220px)'      // single preview capped at ~half modal width
              : `repeat(${Math.min(previews.length, 3)}, 1fr)`,
            justifyContent: previews.length === 1 ? 'center' : 'stretch',
            gap: '10px',
          }}>
            {previews.map((b64, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(34,211,238,0.3)',
                borderRadius: '12px', padding: '8px',
              }}>
                <div style={{ aspectRatio: '1', borderRadius: '6px', overflow: 'hidden', background: '#0a0a14' }}>
                  <img src={`data:image/png;base64,${b64}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <button
                  onClick={() => handleSave(b64)}
                  disabled={saving}
                  data-testid={`emote-save-preview-${i}`}
                  style={{
                    width: '100%', marginTop: '8px', padding: '6px 8px',
                    background: 'linear-gradient(135deg, #06b6d4, #c026d3)',
                    border: 'none', borderRadius: '6px', color: '#fff',
                    fontSize: '10px', fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  💾 Save to library
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Library */}
      <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8',
                    letterSpacing: '0.12em', marginTop: '22px', marginBottom: '10px',
                    textTransform: 'uppercase' }}>
        My Emote Library ({myEmotes.length})
      </div>
      {myEmotes.length === 0 ? (
        <div style={{
          padding: '20px', textAlign: 'center', color: '#555', fontSize: '12px',
          border: '1px dashed rgba(255,255,255,0.08)', borderRadius: '10px',
        }}>
          <ImageIcon size={22} style={{ opacity: 0.5, marginBottom: '6px' }} />
          <div>Nothing saved yet — generate your first emote above!</div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
          gap: '10px',
        }}>
          {myEmotes.map(e => (
            <LibraryCard
              key={e.id}
              emote={e}
              onDelete={handleDelete}
              onDownload={handleDownloadPng}
              onTwitch={handleTwitchPack}
              onAddToCustom={handleAddToCustom}
              onShareWithFriend={(em) => setShareEmote(em)}
              premium={premium}
            />
          ))}
        </div>
      )}

      {/* Share with Friend modal */}
      {shareEmote && (
        <ShareEmoteToFriendModal
          emote={shareEmote}
          currentUser={currentUser}
          onClose={() => setShareEmote(null)}
          onSent={(recipient) => {
            setShareEmote(null);
            setSuccessMsg(`Sent "${shareEmote.name}" to ${recipient || 'your friend'} ✓`);
            setTimeout(() => setSuccessMsg(null), 3500);
          }}
          onError={(msg) => setError(msg)}
        />
      )}
    </>
  );
};

// Compact friend-picker for sharing an emote to another user's library.
// Reuses GET /api/users?query=<text> for autocomplete + POST /api/emote-lab/share to send.
const ShareEmoteToFriendModal = ({ emote, currentUser, onClose, onSent, onError }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!query || query.length < 2) { setResults([]); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/api/users?query=${encodeURIComponent(query)}&limit=8`);
        if (!r.ok) throw new Error();
        const data = await r.json();
        const list = Array.isArray(data) ? data : (data?.users || data?.results || []);
        if (!cancelled) setResults(list.filter(u => (u.id || u._id) !== currentUser?.id));
      } catch (_e) {
        if (!cancelled) setResults([]);
      }
      if (!cancelled) setSearching(false);
    }, 220);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, currentUser?.id]);

  const sendTo = async (recipient) => {
    const recipientId = recipient.id || recipient._id;
    if (!recipientId) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/api/emote-lab/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_user_id: currentUser.id,
          recipient_user_id: recipientId,
          source_emote_id: emote.id,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Share failed');
      onSent(recipient.name || recipient.username || 'them');
    } catch (e) {
      onError(e.message || 'Share failed');
    }
    setSending(false);
  };

  return (
    <div
      data-testid="emote-share-friend-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100003,
        background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '380px',
          background: 'linear-gradient(180deg, #0d0d14 0%, #07070b 100%)',
          border: '1px solid rgba(34,211,238,0.25)',
          borderRadius: '14px', padding: '18px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Share2 size={14} style={{ color: '#fbbf24' }} />
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: '#fff' }}>Share with a friend</h3>
          </div>
          <button onClick={onClose} data-testid="emote-share-close"
                  style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>

        {/* Emote preview row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px',
                      padding: '8px', borderRadius: '10px', background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '6px', overflow: 'hidden',
                        background: '#0a0a14', border: '1px solid rgba(34,211,238,0.18)' }}>
            <img src={`${API}/api/emote-lab/${emote.id}/file`} alt={emote.name}
                 style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: '12px', fontWeight: 700,
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {emote.name}
            </div>
            <div style={{ fontSize: '9px', color: '#64748b', textTransform: 'uppercase',
                          letterSpacing: '0.1em', marginTop: '2px' }}>
              {emote.style}
            </div>
          </div>
        </div>

        <input
          type="text"
          data-testid="emote-share-search"
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Type a name or @username…"
          style={{
            width: '100%', boxSizing: 'border-box', padding: '10px 12px',
            background: 'rgba(255,255,255,0.04)', color: '#fff',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px',
            fontSize: '13px', outline: 'none',
          }}
        />

        <div style={{ marginTop: '10px', maxHeight: '260px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {searching && <div style={{ fontSize: '11px', color: '#64748b', padding: '8px' }}>Searching…</div>}
          {!searching && query && results.length === 0 && (
            <div style={{ fontSize: '11px', color: '#64748b', padding: '8px' }}>No matches.</div>
          )}
          {results.map(u => (
            <button
              key={u.id || u._id}
              data-testid={`emote-share-pick-${u.id || u._id}`}
              disabled={sending}
              onClick={() => sendTo(u)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px',
                padding: '8px 10px',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: '8px', cursor: sending ? 'wait' : 'pointer',
                textAlign: 'left',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#22d3ee'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
            >
              {u.avatar ? (
                <img src={u.avatar} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '28px', height: '28px', borderRadius: '50%',
                              background: 'linear-gradient(135deg, #22d3ee, #c026d3)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              color: '#fff', fontSize: '11px', fontWeight: 800 }}>
                  {(u.name || u.username || '?').charAt(0).toUpperCase()}
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#fff', fontSize: '12px', fontWeight: 700,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {u.name || u.username || 'User'}
                </div>
                {u.username && u.name && (
                  <div style={{ color: '#64748b', fontSize: '10px' }}>@{u.username}</div>
                )}
              </div>
              <span style={{ fontSize: '10px', color: '#22d3ee', fontWeight: 700,
                              letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Send →
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const lbl = {
  display: 'block', fontSize: '10px',
  textTransform: 'uppercase', letterSpacing: '0.12em',
  color: '#94a3b8', marginBottom: '6px', marginTop: '4px',
  fontWeight: 700,
};

// =============== INLINE PANEL (embed inside DJ Tools tab) ===============
export const EmoteLabInline = ({ currentUser }) => {
  return (
    <div
      data-testid="emote-lab-inline"
      style={{
        width: '100%', maxWidth: '780px', margin: '0 auto', padding: '18px',
        background: 'linear-gradient(180deg, #0d0d14 0%, #07070b 100%)',
        border: '1px solid rgba(34,211,238,0.2)', borderRadius: '16px',
        boxShadow: '0 0 0 1px rgba(192,38,211,0.12), 0 18px 40px rgba(0,0,0,0.5)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
        <Wand2 size={20} style={{ color: '#22d3ee' }} />
        <div>
          <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800,
                       background: 'linear-gradient(135deg, #22d3ee, #e879f9)',
                       WebkitBackgroundClip: 'text', backgroundClip: 'text',
                       WebkitTextFillColor: 'transparent',
                       letterSpacing: '0.04em' }}>
            BEATSEEK EMOTE LAB
          </h3>
          <div style={{ fontSize: '10px', color: '#94a3b8', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Premium · AI Twitch / Discord Emote Generator
          </div>
        </div>
      </div>
      <EmoteLabBody currentUser={currentUser} premium={true} onClose={() => {}} />
    </div>
  );
};

// =============== PREMIUM MODAL (DJ Tools / Streamer) ===============
export const EmoteLabModal = ({ isOpen, onClose, currentUser }) => {
  if (!isOpen) return null;
  return (
    <div
      data-testid="emote-lab-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '720px', maxHeight: '92vh', overflowY: 'auto',
          background: 'linear-gradient(180deg, #0d0d14 0%, #07070b 100%)',
          border: '1px solid rgba(34,211,238,0.25)',
          borderRadius: '18px', padding: '22px',
          boxShadow: '0 30px 72px rgba(0,0,0,0.65), 0 0 0 1px rgba(192,38,211,0.18)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Wand2 size={20} style={{ color: '#22d3ee' }} />
            <div>
              <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 800,
                           background: 'linear-gradient(135deg, #22d3ee, #e879f9)',
                           WebkitBackgroundClip: 'text', backgroundClip: 'text',
                           WebkitTextFillColor: 'transparent',
                           letterSpacing: '0.04em' }}>
                BEATSEEK EMOTE LAB
              </h2>
              <div style={{ fontSize: '10px', color: '#94a3b8', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                Premium · DJ + Streamer
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="emote-lab-close"
            style={{
              width: 30, height: 30, borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: '#aaa', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>
        <EmoteLabBody currentUser={currentUser} premium={true} onClose={onClose} />
      </div>
    </div>
  );
};

// =============== LITE MODAL (Dashboard / My Emotes) ===============
export const EmoteLabLite = ({ isOpen, onClose, currentUser, initialPrompt, initialStyle }) => {
  if (!isOpen) return null;
  return (
    <div
      data-testid="emote-lab-lite-modal"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100002,
        background: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '14px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '520px', maxHeight: '88vh', overflowY: 'auto',
          background: 'linear-gradient(180deg, #0d0d14 0%, #07070b 100%)',
          border: '1px solid rgba(34,211,238,0.2)',
          borderRadius: '16px', padding: '20px',
          boxShadow: '0 24px 60px rgba(0,0,0,0.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Sparkles size={16} style={{ color: '#22d3ee' }} />
            <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 800, color: '#fff' }}>
              The Emote Lab
            </h3>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            data-testid="emote-lab-lite-close"
            style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
              color: '#aaa', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={13} />
          </button>
        </div>
        <EmoteLabBody currentUser={currentUser} premium={false} onClose={onClose} initialPrompt={initialPrompt} initialStyle={initialStyle} />
      </div>
    </div>
  );
};

export default EmoteLabModal;
